/*
 * Tokenizer fuer die Rechtschreibpruefung.
 *
 * Pure Functions, keine Side-Effects, keine Worker-Awareness — wird sowohl im
 * Worker (vor jedem hunspell-asm Lookup) als auch im Main-Thread (fuer
 * SpellCheckMenu-Routing, Wort-Selection-Erkennung) genutzt.
 *
 * Verantwortlichkeiten:
 *   1. Wort-Tokenisierung mit Unicode-Property-Klassen (\p{L}-basiert)
 *   2. classifyCase() — Klassifikation der Gross-/Kleinschreibung in 7 Klassen,
 *      damit Tippfehler-Pattern wie "FEhler" und "etWas" markiert, aber Marken-
 *      Namen wie "PowerPoint" via Camel-Splitting akzeptiert werden.
 *   3. splitCamelCase() — splittet ein Wort an Gross-Klein-Grenzen.
 *   4. buildSkipRanges() — Bereiche im Paragraph-Text, in denen NICHT
 *      tokenisiert wird (Inline-Code, URLs, E-Mails, Versions-Tags etc.).
 *   5. wordRangeAt() — findet das Wort an einer Cursor-Position.
 *   6. isSingleWordSelection() — fuer das ContextMenu-Routing: ist die
 *      aktuelle Selektion leer oder umfasst genau ein Wort?
 */

// Wort = Letter-Sequenz, optional mit internen Bindestrichen oder Apostrophen,
// optional mit einem trailing Punkt fuer Abkuerzungen (`bzw.`, `usw.`, `vgl.`).
// Hunspell hat solche Abkuerzungen MIT Punkt im Wörterbuch und akzeptiert
// `bzw` ohne Punkt nicht. Ohne den Trailing-Dot-Match wuerden die ersten drei
// Buchstaben dauerhaft als Tippfehler markiert (Lesson Phase 3).
// Unicode `\p{L}` deckt Latin, Umlaute, Akzente, Kyrillisch, Griechisch etc.
// `’` ist der typografische Apostroph (’), Backslash-Hyphen escaped den
// regulaeren Bindestrich. Bindestriche/Apostrophe nur INTERN (zwischen Letters),
// nicht am Wortanfang/-ende — das vermeidet False-Positives bei Satzzeichen.
const WORD_RE = /\p{L}+(?:[-'’]\p{L}+)*\.?/gu

// Selbiges Pattern, aber als Anchor fuer "ist genau das ganze Wort?".
const SINGLE_WORD_RE = /^\p{L}+(?:[-'’]\p{L}+)*\.?$/u

// Skip-Erkenner: Sequenzen, die nie ein "Wort" im Sinn der Rechtschreibpruefung
// sind. Wir matchen sie BEVOR wir tokenisieren und markieren ihre Bereiche als
// Skip — der Wort-Tokenizer respektiert diese Bereiche dann.
const URL_RE   = /https?:\/\/\S+|www\.\S+/g
const EMAIL_RE = /\S+@\S+\.\S+/g
// Token mit mindestens einer Ziffer (Versions-Tags wie v2.0, IDs wie K9).
// Gemischter Letter-/Ziffer-Token. Wir matchen `\S+` dort, wo eine Ziffer drin
// ist — ein einzelner Buchstabenanteil ginge sonst in den Wort-Tokenizer.
const TOKEN_WITH_DIGIT_RE = /\S*\d\S*/g

// Multi-Punkt-Abkuerzungen (`z.B.`, `u.a.`, `o.Ae.`, `s.o.`, …). Mindestens
// ZWEI Buchstaben-Punkt-Pairs ohne Whitespace dazwischen — `vgl.` mit nur
// einem Punkt wird nicht erfasst (das matched WORD_RE als ein Token und
// Hunspell kennt die einzelnen Abkuerzungen mit Punkt). Skip vermeidet, dass
// `z.B.` als zwei getrennte "z." und "B."-Tokens an Hunspell gehen — beides
// stehen einzeln nicht im Woerterbuch und wuerden als Tippfehler markiert.
const MULTI_DOT_ABBR_RE = /\p{L}+\.(?:\p{L}+\.)+/gu

// Aufzaehlung aller Klassifikations-Klassen, von classifyCase() zurueckgegeben.
export const CaseClass = Object.freeze({
    LOWER:               'lower',                 // "haus", "regen"
    UPPER:               'upper',                 // "HTTP", "IONOS", "AG"
    TITLECASE:           'titlecase',             // "Haus", "Berlin"
    CAPSLOCK_SLIP:       'capslock_slip',         // "FEhler", "ETwas" (Tippfehler)
    LOWER_START_CAPITAL: 'lower_start_capital',   // "etWas", "iOS" (Tippfehler / Brand)
    CAMELCASE:           'camelcase',             // "PowerPoint", "JavaScript"
    UNUSUAL:             'unusual'                // sonstige weirde Faelle, skip
})

/**
 * Tokenisiert einen Text in Woerter.
 *
 * @param {string} text
 * @param {Array<{start:number, length:number}>} [skipRanges]
 *        Bereiche, in denen nicht tokenisiert werden soll (sortiert, nicht-
 *        ueberlappend). Tokens, deren Range in einem Skip-Range LIEGT (auch
 *        teilweise), werden uebersprungen.
 * @returns {Array<{word:string, start:number, length:number}>}
 */
export function tokenizeWords(text, skipRanges = null) {
    const out = []
    if (!text) return out

    WORD_RE.lastIndex = 0   // globale Regex: lastIndex vor jedem Lauf zuruecksetzen
    let m
    while ((m = WORD_RE.exec(text)) !== null) {
        const start = m.index
        const length = m[0].length
        if (skipRanges && overlapsAnyRange(skipRanges, start, length)) continue
        out.push({ word: m[0], start, length })
    }
    return out
}

/**
 * Klassifiziert ein Wort nach Gross-/Kleinschreibung.
 * @param {string} word
 * @returns {string} eine der CaseClass-Konstanten
 */
export function classifyCase(word) {
    if (!word) return CaseClass.UNUSUAL

    const lower = word.toLowerCase()
    const upper = word.toUpperCase()

    if (word === lower) return CaseClass.LOWER
    if (word === upper) return CaseClass.UPPER

    // Pattern-Tests gegen Unicode-Property-Klassen.
    // \p{Lu} = Letter Uppercase, \p{Ll} = Letter Lowercase
    // Trailing-Dot fuer Abkuerzungen (`Vgl.`, `Bzw.`) optional zulassen, sonst
    // wuerden TitleCase-Abkuerzungen als UNUSUAL klassifiziert und stumm
    // geskipt — das WORD_RE matched sie aber jetzt mit Punkt.
    if (/^\p{Lu}\p{Ll}+\.?$/u.test(word)) return CaseClass.TITLECASE
    if (/^\p{Lu}{2,}\p{Ll}+\.?$/u.test(word)) return CaseClass.CAPSLOCK_SLIP
    if (/^\p{Ll}+\p{Lu}/u.test(word)) return CaseClass.LOWER_START_CAPITAL
    if (/^\p{Lu}\p{Ll}+\p{Lu}/u.test(word)) return CaseClass.CAMELCASE

    return CaseClass.UNUSUAL
}

/**
 * Splittet ein Wort an lower→Upper-Grenzen.
 *
 * Nur fuer CAMELCASE und LOWER_START_CAPITAL relevant — also Klassen, in denen
 * die Splitting-Strategie (jeden Teil im Dict pruefen) ein nuetzliches Resultat
 * liefern KANN. Fuer CAPSLOCK_SLIP wird das Wort sowieso immer als Tippfehler
 * markiert; ein Split waere dort nur Komplexitaet ohne Nutzen.
 *
 *   "PowerPoint"  -> ["Power", "Point"]
 *   "JavaScript"  -> ["Java", "Script"]
 *   "etWas"       -> ["et", "Was"]
 *   "iOS"         -> ["i", "OS"]
 *   "MyFooBar"    -> ["My", "Foo", "Bar"]
 *   "FEhler"      -> ["FEhler"]    (kein lower->Upper, also kein Split)
 *   "haus"        -> ["haus"]
 *
 * @param {string} word
 * @returns {string[]}
 */
export function splitCamelCase(word) {
    if (!word) return []
    const parts = []
    let cur = word[0]
    for (let i = 1; i < word.length; i++) {
        const c  = word[i]
        const pv = word[i - 1]
        // Uebergang lower -> Upper: hier splitten. Andere Uebergaenge sind
        // entweder unkritisch (Upper->Upper, lower->lower) oder gehoeren zum
        // selben "Wort-Stueck" (Upper->lower wie in 'PowerPoint' nach 'P').
        if (/\p{Ll}/u.test(pv) && /\p{Lu}/u.test(c)) {
            parts.push(cur)
            cur = c
        } else {
            cur += c
        }
    }
    if (cur) parts.push(cur)
    return parts
}

/**
 * Baut Skip-Ranges fuer einen Paragraph-Text.
 * Berücksichtigt Inline-Code-Spans (aus formatSpans), URLs, E-Mails und Tokens
 * mit Ziffern. Code-Block-Paragraphs werden nicht hier behandelt (der Manager
 * skipt diese komplett, kein Worker-Job).
 *
 * @param {string} text
 * @param {Array<{start:number, end:number, format:string}>} [formatSpans]
 *        FormatSpans aus dem Paragraph-Modell. Spans mit format === 'code'
 *        werden zu Skip-Ranges.
 * @returns {Array<{start:number, length:number}>}
 *          sortiert nach start, nicht-ueberlappend, gemerged
 */
export function buildSkipRanges(text, formatSpans = null) {
    if (!text) return []
    const ranges = []

    // 1) Inline-Code-Spans aus formatSpans
    if (formatSpans) {
        for (const s of formatSpans) {
            if (s.format !== 'code') continue
            const start = Math.max(0, s.start)
            const end   = Math.min(text.length, s.end)
            if (end > start) ranges.push({ start, length: end - start })
        }
    }

    // 2) URLs, E-Mails, Tokens mit Ziffern, Multi-Punkt-Abkuerzungen
    pushAllMatches(ranges, text, URL_RE)
    pushAllMatches(ranges, text, EMAIL_RE)
    pushAllMatches(ranges, text, TOKEN_WITH_DIGIT_RE)
    pushAllMatches(ranges, text, MULTI_DOT_ABBR_RE)

    return mergeRanges(ranges)
}

/**
 * Findet das Wort an einer Position (Cursor- oder Klick-Position).
 * Falls offset auf Whitespace zeigt, wird das links angrenzende Wort
 * zurueckgegeben — Browser-Konvention fuer "Wort am Cursor". Wenn auch
 * links nichts ist: null.
 *
 * @param {string} text
 * @param {number} offset
 * @returns {{word:string, start:number, length:number} | null}
 */
export function wordRangeAt(text, offset) {
    if (!text) return null
    const len = text.length
    let pos = Math.max(0, Math.min(len, offset))

    // Wenn pos auf Whitespace zeigt, einen Schritt nach links — User hat
    // hinter das Wort geklickt.
    if (pos === len || !isWordChar(text[pos])) {
        if (pos > 0 && isWordChar(text[pos - 1])) pos--
        else return null
    }

    // Linke Wort-Grenze suchen
    let start = pos
    while (start > 0 && isWordChar(text[start - 1])) start--
    // Rechte Wort-Grenze suchen
    let end = pos
    while (end < len && isWordChar(text[end])) end++

    if (end <= start) return null
    return { word: text.slice(start, end), start, length: end - start }
}

/**
 * Prueft, ob die aktuelle Selektion leer ist oder GENAU ein Wort umfasst.
 * Whitespace-Padding ist okay, Interpunktion zaehlt nicht als Wort.
 *
 * Wird vom ContextMenu-Routing (mouseHandlers.js) genutzt, um zu entscheiden,
 * ob das Spellcheck-Menue oder das regulaere ContextMenu erscheint.
 *
 * @param {string} text
 * @param {number} selStart
 * @param {number} selEnd
 * @returns {boolean}
 */
export function isSingleWordSelection(text, selStart, selEnd) {
    if (selStart === selEnd) return true        // leere Selektion = "kein Wort drueber"
    const lo = Math.min(selStart, selEnd)
    const hi = Math.max(selStart, selEnd)
    const slice = text.slice(lo, hi).trim()
    return slice.length > 0 && SINGLE_WORD_RE.test(slice)
}

// ============================================================================
// interne Helpers
// ============================================================================

function isWordChar(ch) {
    if (!ch) return false
    return /[\p{L}\p{M}'’\-]/u.test(ch)
}

function pushAllMatches(ranges, text, regex) {
    regex.lastIndex = 0
    let m
    while ((m = regex.exec(text)) !== null) {
        if (m[0].length === 0) { regex.lastIndex++; continue }
        ranges.push({ start: m.index, length: m[0].length })
    }
}

function overlapsAnyRange(ranges, start, length) {
    const end = start + length
    for (const r of ranges) {
        const rEnd = r.start + r.length
        if (start < rEnd && end > r.start) return true
    }
    return false
}

function mergeRanges(ranges) {
    if (ranges.length <= 1) return ranges.slice()
    const sorted = ranges.slice().sort((a, b) => a.start - b.start)
    const out = [{ ...sorted[0] }]
    for (let i = 1; i < sorted.length; i++) {
        const last = out[out.length - 1]
        const cur  = sorted[i]
        const lastEnd = last.start + last.length
        if (cur.start <= lastEnd) {
            const newEnd = Math.max(lastEnd, cur.start + cur.length)
            last.length = newEnd - last.start
        } else {
            out.push({ ...cur })
        }
    }
    return out
}
